import { Injectable } from '@angular/core';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core'; // Import TranslateService

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  constructor(private translate: TranslateService) {} // Inject TranslateService

  private showAlert(options: SweetAlertOptions) {
    return Swal.fire(options);
  }

  confirm(title: string, text: string, onConfirm: () => void): void {
    // Fetch translation for the confirm and cancel buttons
    this.translate.get(['ALERT.CONFIRM_DELETE', 'ALERT.CANCEL']).subscribe(translations => {
      const options: SweetAlertOptions = {
        title,
        text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: translations['ALERT.CONFIRM_DELETE'], // Translated confirm button text
        cancelButtonText: translations['ALERT.CANCEL'] // Translated cancel button text
      };
      Swal.fire(options).then((result) => {
        if (result.isConfirmed) {
          onConfirm();
        }
      });
    });
  }

  confirmOverwriteOrAdd(
    title: string,
    text: string,
    onOverwrite: () => void,
    onAdd: () => void,
    onCancel?: () => void
  ): void {
    this.translate.get(['ALERT.OVERWRITE', 'ALERT.ADD_NEW', 'ALERT.CANCEL']).subscribe(translations => {
      const options: SweetAlertOptions = {
        title,
        text,
        icon: 'warning',
        showCancelButton: true,
        showDenyButton: true, // Add a second button for "Add New"
        confirmButtonColor: '#3085d6',
        denyButtonColor: '#28a745',
        cancelButtonColor: '#d33',
        confirmButtonText: translations['ALERT.OVERWRITE'], // "Tak, nadpisz"
        denyButtonText: translations['ALERT.ADD_NEW'], // "Tak, dodaj"
        cancelButtonText: translations['ALERT.CANCEL'] // "Anuluj"
      };
  
      Swal.fire(options).then((result) => {
        if (result.isConfirmed) {
          onOverwrite();
        } else if (result.isDenied) {
          onAdd();
        } else if (result.dismiss === Swal.DismissReason.cancel && onCancel) {
          onCancel();
        }
      });
    });
  }
  

  success(message: string) {
    // Translate success title
    this.translate.get('ALERT.SUCCESS').subscribe((successTitle) => {
      return this.showAlert({
        title: successTitle,
        text: message,
        icon: 'success',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK',
        timer: 2000,
        timerProgressBar: true
      });
    });
  }

  warning(message: string) {
    // Translate warning title
    this.translate.get('ALERT.WARNING').subscribe((warningTitle) => {
      return this.showAlert({
        title: warningTitle, // Translated warning title
        text: message,
        icon: 'warning',
        confirmButtonColor: '#FFBB35',
        confirmButtonText: 'OK'
      });
    });
  }

  error(message: string) {
    // Translate error title
    this.translate.get('ALERT.ERROR').subscribe((errorTitle) => {
      return this.showAlert({
        title: errorTitle, // Translated error title
        text: message,
        icon: 'error',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    });
  }

  unsaved(onConfirm: () => void, onCancel?: () => void): void {
    this.translate.get(['ALERT.UNSAVED_CHANGES', 'ALERT.UNSAVED_PROMPT', 'ALERT.YES', 'ALERT.NO']).subscribe(translations => {
      const options: SweetAlertOptions = {
        title: translations['ALERT.UNSAVED_CHANGES'],
        text: translations['ALERT.UNSAVED_PROMPT'],
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: translations['ALERT.YES'], // Save Changes
        cancelButtonText: translations['ALERT.NO'], // Discard Changes
      };
      Swal.fire(options).then((result) => {
        if (result.isConfirmed) {
          onConfirm(); // Execute the confirm callback
        } else if (result.dismiss === Swal.DismissReason.cancel && onCancel) {
          onCancel(); // Execute the cancel callback if provided
        }
      });
    });
  }
  

  info(message: string) {
    // Translate info title
    this.translate.get('ALERT.INFO').subscribe((infoTitle) => {
      return this.showAlert({
        title: infoTitle, // Translated info title
        text: message,
        icon: 'info',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
      });
    });
  }
  
  
}
